/**
 * Luxury Gold Shop Theme Enhancements
 * Interactive JavaScript for sophisticated user experience
 */

class LuxuryEnhancements {
  constructor() {
    this.initScrollReveal();
    this.initParallaxEffects();
    this.initGoldShimmerEffects();
    this.initSmoothScrolling();
    this.initProductImageEnhancements();
  }

  // Scroll reveal animations for luxury elements
  initScrollReveal() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-reveal class
    document.querySelectorAll('.scroll-reveal').forEach(el => {
      observer.observe(el);
    });

    // Add scroll-reveal class to product cards and key elements
    document.querySelectorAll('.card-wrapper, .product__info-wrapper, .footer-block').forEach(el => {
      el.classList.add('scroll-reveal');
      observer.observe(el);
    });
  }

  // Parallax effects for background elements
  initParallaxEffects() {
    let ticking = false;

    const updateParallax = () => {
      const scrolled = window.pageYOffset;
      const parallaxElements = document.querySelectorAll('.luxury-background');
      
      parallaxElements.forEach(element => {
        const speed = 0.5;
        const yPos = -(scrolled * speed);
        element.style.transform = `translateY(${yPos}px)`;
      });
      
      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick);
  }

  // Enhanced gold shimmer effects
  initGoldShimmerEffects() {

    // Animate gold accents on product titles
    document.querySelectorAll('.card__heading').forEach(heading => {
      const words = heading.textContent.split(' ');
      if (words.length > 1) {
        // Make the last word gold-accented
        const lastWordIndex = words.length - 1;
        words[lastWordIndex] = `<span class="gold-accent">${words[lastWordIndex]}</span>`;
        heading.innerHTML = words.join(' ');
      }
    });

    // Enhanced navigation effects
    this.initNavigationEffects();
  }

  // Navigation interactive effects
  initNavigationEffects() {
    // Add current page highlighting
    const currentPath = window.location.pathname;
    document.querySelectorAll('.nav-link').forEach(link => {
      if (link.getAttribute('href') === currentPath) {
        link.closest('.nav-item').classList.add('current');
      }
    });

    // Smooth hover effects for navigation
    document.querySelectorAll('.nav-item').forEach(item => {
      item.addEventListener('mouseenter', () => {
        item.style.transform = 'translateY(-1px)';
      });

      item.addEventListener('mouseleave', () => {
        item.style.transform = 'translateY(0)';
      });
    });

    // Sticky navigation effect on scroll
    let lastScrollTop = 0;
    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const navigation = document.querySelector('.main-navigation');
      
      if (navigation) {
        if (scrollTop > 100) {
          navigation.style.position = 'sticky';
          navigation.style.top = '0';
          navigation.style.zIndex = '999';
          navigation.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.3)';
        } else {
          navigation.style.position = 'relative';
          navigation.style.boxShadow = 'none';
        }
      }
      lastScrollTop = scrollTop;
    });
  }

  // Smooth scrolling for navigation
  initSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // Product image enhancements
  initProductImageEnhancements() {
    // Add luxury gallery class to product media
    document.querySelectorAll('.product__media-wrapper').forEach(wrapper => {
      wrapper.classList.add('product-gallery', 'luxury');
    });

    // Enhanced image loading with fade-in effect
    document.querySelectorAll('.card__media img').forEach(img => {
      img.addEventListener('load', function() {
        this.style.opacity = '0';
        this.style.transition = 'opacity 0.5s ease';
        setTimeout(() => {
          this.style.opacity = '1';
        }, 100);
      });
    });

    // Product image zoom effect
    document.querySelectorAll('.product__media img').forEach(img => {
      let isZoomed = false;
      
      img.addEventListener('click', function(e) {
        if (!isZoomed) {
          this.style.transform = 'scale(1.5)';
          this.style.zIndex = '1000';
          this.style.position = 'relative';
          this.style.transition = 'transform 0.3s ease';
          isZoomed = true;
        } else {
          this.style.transform = 'scale(1)';
          this.style.zIndex = 'auto';
          isZoomed = false;
        }
      });
    });
  }

  // Add luxury loading spinner
  static showLuxuryLoader() {
    const loader = document.createElement('div');
    loader.className = 'luxury-spinner';
    loader.style.position = 'fixed';
    loader.style.top = '50%';
    loader.style.left = '50%';
    loader.style.transform = 'translate(-50%, -50%)';
    loader.style.zIndex = '9999';
    document.body.appendChild(loader);
    
    setTimeout(() => {
      loader.remove();
    }, 2000);
  }

  // Enhanced form interactions
  initLuxuryForms() {
    document.querySelectorAll('.field').forEach(field => {
      field.classList.add('luxury-field');
      
      const input = field.querySelector('input, textarea, select');
      if (input) {
        input.addEventListener('focus', () => {
          field.style.transform = 'translateY(-2px)';
          field.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.2)';
        });
        
        input.addEventListener('blur', () => {
          field.style.transform = 'translateY(0)';
          field.style.boxShadow = 'none';
        });
      }
    });
  }
}

// Initialize luxury enhancements when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new LuxuryEnhancements();
});

// Removed luxury button effects

// Custom cursor for luxury experience
document.addEventListener('DOMContentLoaded', () => {
  const cursor = document.createElement('div');
  cursor.className = 'luxury-cursor';
  cursor.style.cssText = `
    width: 20px;
    height: 20px;
    background: radial-gradient(circle, rgba(212, 175, 55, 0.8) 0%, transparent 70%);
    border-radius: 50%;
    position: fixed;
    pointer-events: none;
    z-index: 9999;
    transform: translate(-50%, -50%);
    transition: all 0.1s ease;
  `;
  document.body.appendChild(cursor);

  document.addEventListener('mousemove', (e) => {
    cursor.style.left = e.clientX + 'px';
    cursor.style.top = e.clientY + 'px';
  });

  // Enhance cursor on interactive elements
  document.querySelectorAll('a, button, .card-wrapper').forEach(el => {
    el.addEventListener('mouseenter', () => {
      cursor.style.transform = 'translate(-50%, -50%) scale(1.5)';
      cursor.style.background = 'radial-gradient(circle, rgba(255, 215, 0, 0.9) 0%, transparent 70%)';
    });
    
    el.addEventListener('mouseleave', () => {
      cursor.style.transform = 'translate(-50%, -50%) scale(1)';
      cursor.style.background = 'radial-gradient(circle, rgba(212, 175, 55, 0.8) 0%, transparent 70%)';
    });
  });
});

// Export for use in other scripts
window.LuxuryEnhancements = LuxuryEnhancements; 